import classNames from "classnames";
import Clickable from "components/Clickable";
import { ButtonText } from "components/Clickable/Buttons";
import { CmsResponsiveImage } from "components/CmsResponsiveImage";
import { imagePlaceholder } from "helpers/utils";
import { BlogPostTile, BlogPostsSubBlock, StreamfieldBlock } from "interfaces";
import { forwardRef } from "react";

import styles from "./blog-grid.module.scss";
import tileStyles from "./blog-tile.module.scss";

interface BlogTileProps extends BlogPostTile {
    isFeatured?: boolean;
    isCategoryPage?: boolean;
}

export type FeaturedBlogPostsBlock = StreamfieldBlock<
    "featured_blog_posts",
    { title: string; posts: BlogPostsSubBlock[] }
>;

export const BlogTile = (props: BlogTileProps) => {
    const categoriesString =
        props.categories.length > 1
            ? props.categories.join(" | ")
            : props.categories[0];
    if (props.isFeatured) {
        return (
            <>
                <Clickable href={props.url} className={styles.clickable}>
                    <CmsResponsiveImage
                        cmsImage={{
                            url: props.primary_image ?? imagePlaceholder,
                        }}
                        className={tileStyles.image}
                        sizes="(min-width: 0px) 100vw, (min-width: 768px) 50vw, (min-width: 1480px) 670px"
                        cldSrcSet={[275, 550, 825, 1100]}
                        cloudinaryProps={{ aspectRatio: "8:5", crop: "fill" }}
                    />
                    {!props.isCategoryPage && (
                        <h4 className={tileStyles.category}>
                            {categoriesString}
                        </h4>
                    )}
                    <h3 className={tileStyles.featuredTitle}>{props.title}</h3>
                </Clickable>
                {props.post_description && (
                    <p className={tileStyles.description}>
                        {props.post_description}
                    </p>
                )}
                <ButtonText className={tileStyles.featuredCta} href={props.url}>
                    Read Now
                </ButtonText>
            </>
        );
    }
    return (
        <>
            <CmsResponsiveImage
                cmsImage={{
                    url: props.primary_image ?? imagePlaceholder,
                }}
                className={tileStyles.image}
                sizes="(min-width: 0px) 100vw, (min-width: 768px) 33vw, (min-width: 1480px) 440px"
                cldSrcSet={[275, 550, 825, 1100]}
                cloudinaryProps={{ aspectRatio: "8:5", crop: "fill" }}
            />
            {!props.isCategoryPage && (
                <h4 className={tileStyles.category}>{categoriesString}</h4>
            )}
            <h3 className={tileStyles.title}>{props.title}</h3>
        </>
    );
};

interface Props {
    tiles: BlogPostTile[];
    count?: number;
    title?: string;
    isFeatureGrid?: boolean;
    isCategoryPage?: boolean;
}

export const BlogPostGrid = forwardRef<HTMLElement, Props>(
    function BlogPostGrid(props, ref) {
        const gridClasses = classNames({
            [styles.grid]: true && !props.isFeatureGrid,
            [styles.featureGrid]: props.isFeatureGrid,
        });

        return (
            <section className={gridClasses} ref={ref}>
                <header className={styles.header}>
                    {props.title && (
                        <h2 className={styles.title}>{props.title}</h2>
                    )}
                    {props.count && (
                        <span className={styles.count}>
                            {props.count} Stories
                        </span>
                    )}
                </header>
                <ul className={styles.list}>
                    {props.tiles.map((tile, index) => {
                        const isFirstFeaturedTile =
                            props.isFeatureGrid && index === 0;
                        if (isFirstFeaturedTile) {
                            return (
                                <li key={tile.id} className={styles.tile}>
                                    <BlogTile
                                        isFeatured={isFirstFeaturedTile}
                                        isCategoryPage={props.isCategoryPage}
                                        {...tile}
                                    />
                                </li>
                            );
                        }
                        return (
                            <li key={tile.id} className={styles.tile}>
                                <Clickable
                                    href={tile.url}
                                    className={styles.clickable}
                                >
                                    <BlogTile
                                        isFeatured={isFirstFeaturedTile}
                                        isCategoryPage={props.isCategoryPage}
                                        {...tile}
                                    />
                                </Clickable>
                            </li>
                        );
                    })}
                </ul>
            </section>
        );
    },
);
